import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogIn, LogOut } from "lucide-react";
import { startCheckout, cancelSubscription } from "../../lib/checkout";
import { useAuth } from "../../context/AuthContext";
import { InstallButton } from "./InstallButton";
import { toast } from "sonner";

export const InnerNav = ({ active }) => {
  const navigate = useNavigate();
  const { user, login, logout, refresh } = useAuth();
  const [loading, setLoading] = useState(false);

  const subscribe = async () => {
    if (!user) {
      login();
      return;
    }
    setLoading(true);
    try {
      startCheckout(user.email, user.user_id);
    } catch {
      toast.error("Could not start checkout.");
      setLoading(false);
    }
  };

  const doCancel = async () => {
    try {
      const data = await cancelSubscription();
      const until = data?.access_until
        ? new Date(data.access_until * 1000).toLocaleDateString()
        : null;
      toast.success(
        until
          ? `Membership canceled. You keep access until ${until}.`
          : "Membership canceled. You keep access until the period ends."
      );
      refresh();
    } catch {
      toast.error("Could not cancel. Please try again.");
    }
  };

  const confirmCancel = () => {
    toast("Cancel your $2/mo membership?", {
      description: "You'll keep access until the end of the current billing period.",
      action: { label: "Confirm", onClick: doCancel },
      duration: 8000,
    });
  };

  return (
    <header className="sticky top-0 z-50 bg-black/70 backdrop-blur-xl border-b border-white/10">
      <div className="max-w-[1600px] mx-auto px-5 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <Link to="/" data-testid="inner-nav-logo" className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full bg-blood animate-flicker" />
          <span className="font-mono text-xs md:text-sm tracking-[0.35em] uppercase text-bone">
            Old&nbsp;Geezer
          </span>
        </Link>

        <div className="flex items-center gap-4 md:gap-7">
          <button
            data-testid="inner-nav-archive"
            onClick={() => navigate("/archive")}
            className={`font-mono text-[11px] tracking-[0.25em] uppercase transition-colors ${
              active === "archive" ? "text-blood" : "text-muted-foreground hover:text-bone"
            }`}
          >
            Archive
          </button>

          {user ? (
            <div className="flex items-center gap-3 md:gap-4">
              <InstallButton variant="compact" label="Install" />
              {!user.subscribed && (
                <button
                  data-testid="inner-nav-subscribe"
                  onClick={subscribe}
                  disabled={loading}
                  className="inline-flex items-center gap-2 border border-white/20 hover:border-blood hover:bg-blood px-4 md:px-5 py-2 md:py-2.5 font-mono text-[11px] tracking-[0.25em] uppercase transition-all duration-300 disabled:opacity-60"
                >
                  {loading ? "Opening…" : "Subscribe $2/mo"}
                </button>
              )}
              {user.subscribed && (
                <>
                  <span className="hidden sm:inline font-mono text-[10px] tracking-[0.2em] uppercase text-blood border border-blood/40 px-2 py-1">
                    Member
                  </span>
                  <button
                    data-testid="cancel-subscription-btn"
                    onClick={confirmCancel}
                    className="hidden sm:inline font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground hover:text-blood transition-colors"
                  >
                    Cancel
                  </button>
                </>
              )}
              {user.picture ? (
                <img src={user.picture} alt="" className="h-8 w-8 rounded-full border border-white/20 object-cover" data-testid="user-avatar" />
              ) : (
                <span className="h-8 w-8 rounded-full bg-secondary border border-white/20 flex items-center justify-center font-mono text-xs text-bone">
                  {(user.name || user.email || "?").slice(0, 1).toUpperCase()}
                </span>
              )}
              <button
                data-testid="logout-btn"
                onClick={logout}
                title="Sign out"
                className="text-muted-foreground hover:text-bone transition-colors"
              >
                <LogOut className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <button
              data-testid="signin-btn"
              onClick={login}
              className="inline-flex items-center gap-2 bg-bone text-black hover:bg-white px-5 py-2.5 font-mono text-[11px] tracking-[0.25em] uppercase transition-colors duration-300"
            >
              <LogIn className="h-4 w-4" /> Sign in
            </button>
          )}
        </div>
      </div>
    </header>
  );
};
